// src/hooks/useQuoteQueries.js
// React Query hooks for Quotes — production grade
// - Stable query keys
// - v5-correct keepPreviousData
// - Lineage actions: convert → job, convert → invoice
// - Cross-invalidation with Work and Invoice roots
// -----------------------------------------------------------------------------

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import * as api from '../services/quoteService';

/* --------------------------------- keys ---------------------------------- */

function stableKey(obj = {}) {
  // Deterministic key for filter objects (avoid cache misses due to identity)
  const entries = Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== '');
  entries.sort(([a], [b]) => (a > b ? 1 : a < b ? -1 : 0));
  const normalized = entries.map(([k, v]) => [k, Array.isArray(v) ? v.join('|') : String(v)]);
  return JSON.stringify(normalized);
}

export const QUOTE_KEYS = {
  root: ['quotes'],
  list: (filters = {}) => ['quotes', 'list', stableKey(filters)],
  one: (id) => ['quote', id],
};

// We avoid importing WORK_KEYS to prevent circular deps
const WORK_ROOT_KEY = ['work'];
const INVOICE_ROOT_KEY = ['invoices'];

/* -------------------------------- queries -------------------------------- */

/**
 * List Quotes with filters/pagination.
 * Service returns { items, meta }.
 */
export function useQuotes(filters = {}, options = {}) {
  const query = useQuery({
    queryKey: QUOTE_KEYS.list(filters),
    queryFn: () => api.listQuotes(filters), // -> { items, meta }
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    ...options,
  });

  const items = query.data?.items ?? [];
  const meta =
    query.data?.meta ?? {
      page: 1,
      limit: items.length,
      total: items.length,
      totalPages: 1,
    };

  return { ...query, items, meta };
}

/**
 * Get a single Quote by id.
 * The service should include lineage fields when present:
 *   { workId?, workNumber?, invoiceId? }
 */
export function useQuote(id, options = {}) {
  return useQuery({
    queryKey: QUOTE_KEYS.one(id),
    queryFn: () => api.getQuote(id), // -> quote object
    enabled: Boolean(id) && (options.enabled ?? true),
    staleTime: 15_000,
    ...options,
  });
}

/* ------------------------------- mutations ------------------------------- */

export function useCreateQuote(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.createQuote,
    onSuccess: (created, vars, ctx) => {
      const id = created?.id || created?._id;
      qc.invalidateQueries({ queryKey: QUOTE_KEYS.root });
      if (id) qc.setQueryData(QUOTE_KEYS.one(id), created);
      options.onSuccess?.(created, vars, ctx);
    },
    onError: (err, vars, ctx) => options.onError?.(err, vars, ctx),
    onSettled: (data, err, vars, ctx) => options.onSettled?.(data, err, vars, ctx),
  });
}

export function useUpdateQuote(id, options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => api.updateQuote(id, payload),
    onSuccess: (updated, vars, ctx) => {
      qc.invalidateQueries({ queryKey: QUOTE_KEYS.root });
      qc.setQueryData(QUOTE_KEYS.one(id), updated);
      options.onSuccess?.(updated, vars, ctx);
    },
    onError: (err, vars, ctx) => {
      // OCC conflict → refetch latest
      if (err?.response?.status === 409) {
        qc.invalidateQueries({ queryKey: QUOTE_KEYS.one(id) });
      }
      options.onError?.(err, vars, ctx);
    },
    onSettled: (data, err, vars, ctx) => options.onSettled?.(data, err, vars, ctx),
  });
}

export function useArchiveQuote(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.archiveQuote,
    onSuccess: (res, vars, ctx) => {
      qc.invalidateQueries({ queryKey: QUOTE_KEYS.root });
      options.onSuccess?.(res, vars, ctx);
    },
    onError: (err, vars, ctx) => options.onError?.(err, vars, ctx),
    onSettled: (data, err, vars, ctx) => options.onSettled?.(data, err, vars, ctx),
  });
}

/* --------------------------- lifecycle actions --------------------------- */

/**
 * Accept Quote (status → accepted)
 */
export function useAcceptQuote(id, options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => api.acceptQuote(id, payload),
    onSuccess: (res, vars, ctx) => {
      qc.invalidateQueries({ queryKey: QUOTE_KEYS.root });
      qc.invalidateQueries({ queryKey: QUOTE_KEYS.one(id) });
      options.onSuccess?.(res, vars, ctx);
    },
    onError: (err, vars, ctx) => options.onError?.(err, vars, ctx),
    onSettled: (data, err, vars, ctx) => options.onSettled?.(data, err, vars, ctx),
  });
}

/**
 * Decline Quote (status → declined)
 */
export function useDeclineQuote(id, options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => api.declineQuote(id, payload),
    onSuccess: (res, vars, ctx) => {
      qc.invalidateQueries({ queryKey: QUOTE_KEYS.root });
      qc.invalidateQueries({ queryKey: QUOTE_KEYS.one(id) });
      options.onSuccess?.(res, vars, ctx);
    },
    onError: (err, vars, ctx) => options.onError?.(err, vars, ctx),
    onSettled: (data, err, vars, ctx) => options.onSettled?.(data, err, vars, ctx),
  });
}

/**
 * Cancel Quote (status → cancelled)
 */
export function useCancelQuote(id, options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => api.cancelQuote(id, payload),
    onSuccess: (res, vars, ctx) => {
      qc.invalidateQueries({ queryKey: QUOTE_KEYS.root });
      qc.invalidateQueries({ queryKey: QUOTE_KEYS.one(id) });
      options.onSuccess?.(res, vars, ctx);
    },
    onError: (err, vars, ctx) => options.onError?.(err, vars, ctx),
    onSettled: (data, err, vars, ctx) => options.onSettled?.(data, err, vars, ctx),
  });
}

/**
 * Convert Quote → Invoice
 * Expected API result: { invoiceId, invoiceNumber, quoteId, workId?, workNumber? }
 */
export function useConvertQuoteToInvoice(id, options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => api.convertToInvoice(id, payload),
    onSuccess: (result, vars, ctx) => {
      // Quote transitions to "converted" and a new invoice appears → refresh both
      qc.invalidateQueries({ queryKey: QUOTE_KEYS.root });
      qc.invalidateQueries({ queryKey: QUOTE_KEYS.one(id) });
      qc.invalidateQueries({ queryKey: INVOICE_ROOT_KEY });
      // If it also linked a job, refresh Work lists just in case
      if (result?.workId || result?.workNumber) {
        qc.invalidateQueries({ queryKey: WORK_ROOT_KEY });
      }
      options.onSuccess?.(result, vars, ctx);
    },
    onError: (err, vars, ctx) => {
      if (err?.response?.status === 409) {
        qc.invalidateQueries({ queryKey: QUOTE_KEYS.one(id) });
      }
      options.onError?.(err, vars, ctx);
    },
    onSettled: (data, err, vars, ctx) => options.onSettled?.(data, err, vars, ctx),
  });
}

/**
 * Convert Quote → Job
 * Expected API result: { workId, workNumber, quoteId }
 */
export function useConvertQuoteToJob(id, options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => api.convertToJob(id, payload),
    onSuccess: (result, vars, ctx) => {
      // Quote is now linked to a Job → refresh quotes + work
      qc.invalidateQueries({ queryKey: QUOTE_KEYS.root });
      qc.invalidateQueries({ queryKey: QUOTE_KEYS.one(id) });
      qc.invalidateQueries({ queryKey: WORK_ROOT_KEY });
      options.onSuccess?.(result, vars, ctx);
    },
    onError: (err, vars, ctx) => {
      if (err?.response?.status === 409) {
        qc.invalidateQueries({ queryKey: QUOTE_KEYS.one(id) });
      }
      options.onError?.(err, vars, ctx);
    },
    onSettled: (data, err, vars, ctx) => options.onSettled?.(data, err, vars, ctx),
  });
}
